import { Button, Form, Space } from "antd";
import TextArea from "antd/es/input/TextArea";
import { useEffect } from "react";
import { useParams } from "react-router-dom";
import {
  useUpdateLeaveApplicationApprovedMutation,
  useUpdateLeaveApplicationCancelMutation,
  useUpdateLeaveApplicationRejectMutation,
} from "../../../features/leaveApplication/leaveApplicationApi";
import useError from "../../../hooks/useError";
import { toast } from "../../../lib/toast";
import { LeaveConfirmModalType } from "../../../pages/admin/Leave/LeaveRequestDetails";
import { ModalComponentProps } from "../../../types";
import { capitalize } from "../../../utils/string";
import CustomModal from "../../ui/CustomModal";

export default function LeaveConfirmModal({
  handleClose,
  data,
  type,
  ...rest
}: ModalComponentProps<boolean, LeaveConfirmModalType>) {
  const [form] = Form.useForm();
  const { viewId } = useParams();

  const [
    updateLeaveApplicationReject,
    { isSuccess, isLoading, isError, error },
  ] = useUpdateLeaveApplicationRejectMutation();
  const [
    updateLeaveApplicationApproved,
    {
      isSuccess: isApprovedSuccess,
      isLoading: isApprovedLoading,
      isError: isApprovedError,
      error: approvedError,
    },
  ] = useUpdateLeaveApplicationApprovedMutation();
  const [
    updateLeaveApplicationCancel,
    {
      isSuccess: isCancelSuccess,
      isLoading: isCancelLoading,
      isError: isCancelError,
      error: cancelError,
    },
  ] = useUpdateLeaveApplicationCancelMutation();
  useError(isError, error, "Leave application reject fail");
  useError(isApprovedError, approvedError, "Leave application approve fail");
  useError(isCancelError, cancelError, "Leave application cancel fail");

  const onFinish = (values: any) => {
    const newData = {
      ...values,
      is_admin_action: data,
    };

    if (viewId) {
      switch (type) {
        case "APPROVE":
          updateLeaveApplicationApproved({
            id: Number(viewId),
            body: { ...newData, status: "accepted" },
          });
          break;

        case "REJECT":
          updateLeaveApplicationReject({
            id: Number(viewId),
            body: {
              ...newData,
              note: newData?.reasons,
              reasons: [
                "violation_of_leave_policy",
                "not_a_valid_leave_request",
                "very_frequent_leave_requests",
                "other",
              ],
              status: "rejected",
            },
          });
          break;

        case "CANCEL":
          updateLeaveApplicationCancel({
            id: Number(viewId),
            body: { ...newData, status: "canceled" },
          });
          break;
      }
    }
  };

  // success toast message
  useEffect(() => {
    if (isSuccess) {
      toast("success", "Leave application reject successfully");
      return handleClose();
    }
  }, [handleClose, isSuccess]);

  useEffect(() => {
    if (isApprovedSuccess) {
      toast("success", "Leave application approved successfully");
      return handleClose();
    }
  }, [handleClose, isApprovedSuccess]);

  useEffect(() => {
    if (isCancelSuccess) {
      toast("success", "Leave application cancel successfully");
      return handleClose();
    }
  }, [handleClose, isCancelSuccess]);

  return (
    <CustomModal
      title={`Leave Application ${capitalize(type as string)}`}
      {...rest}
      handleClose={handleClose}
      width={460}
      footer={null}
    >
      <Form
        className="custom-form"
        layout="vertical"
        requiredMark="optional"
        size="large"
        onFinish={onFinish}
        form={form}
      >
        {type === "APPROVE" && data === false ? null : (
          <Form.Item
            name="reason"
            label="Reason"
            className="mb-4"
            rules={[
              {
                required: true,
                message: "Please enter note",
              },
            ]}
          >
            <TextArea rows={4} size="large" placeholder="Reason..." />
          </Form.Item>
        )}

        <Space className="w-full mt-2" size={12}>
          <Button
            className="normal-dark-button modal-button"
            onClick={handleClose}
            size="middle"
          >
            Cancel
          </Button>

          <Button
            className="modal-button"
            type="primary"
            htmlType="submit"
            size="middle"
            loading={isLoading || isApprovedLoading || isCancelLoading}
          >
            {type === "APPROVE"
              ? "Approve"
              : type === "REJECT"
              ? "Reject"
              : "Ok"}
          </Button>
        </Space>
      </Form>
    </CustomModal>
  );
}
